import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft, ChevronRight, Plus, Loader2 } from 'lucide-react'
import { useCases } from '../../../hooks/useCases'
import { functionsClient } from '../../../lib/functionsClient'
import CaseListItem from './CaseListItem'
import { ConfirmationModal } from '../common/ConfirmationModal'
import styles from './CaseListSidebar.module.css'

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _Loader2 = Loader2 // Keep for potential future use

interface CaseListSidebarProps {
  className?: string
  collapsed?: boolean
  onToggleCollapse?: () => void
}

const CaseListSidebar: React.FC<CaseListSidebarProps> = ({
  className,
  collapsed = false,
  onToggleCollapse,
}) => {
  const navigate = useNavigate()
  const { cases, loading, error } = useCases()

  const [deleteTarget, setDeleteTarget] = useState<string | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const handleCreateCase = () => {
    navigate('/quick-check/new')
  }

  const handleDeleteCase = (caseId: string) => {
    setDeleteTarget(caseId)
  }

  const confirmDelete = async () => {
    if (!deleteTarget) return

    setIsDeleting(true)
    try {
      await functionsClient.deleteCase(deleteTarget)
      setDeleteTarget(null)
      // The case will be automatically removed via Firestore subscription
    } catch (err) {
      console.error('Failed to delete case:', err)
    } finally {
      setIsDeleting(false)
    }
  }

  const cancelDelete = () => {
    setDeleteTarget(null)
  }

  const deletingCase = deleteTarget ? cases.find(c => c.id === deleteTarget) : null

  return (
    <div className={`${styles.sidebar} ${collapsed ? styles.collapsed : ''} ${className || ''}`}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.titleRow}>
          {!collapsed && (
            <>
              <h2 className={styles.title}>Cases</h2>
              <button
                className={styles.newButton}
                onClick={handleCreateCase}
                title="New Case"
              >
                <Plus size={16} />
              </button>
            </>
          )}
          {onToggleCollapse && (
            <button
              className={styles.collapseButton}
              onClick={onToggleCollapse}
              title={collapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
            >
              {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
            </button>
          )}
        </div>
      </div>

      {/* Cases List */}
      {!collapsed && (
        <div className={styles.casesList}>
          {loading ? (
            <div className={styles.loadingState}>
              <Loader2 size={24} className={styles.spinning} />
              <span>Loading cases...</span>
            </div>
          ) : error ? (
            <div className={styles.errorState}>
              <p>Failed to load cases</p>
              <span className={styles.errorDetail}>{error.message}</span>
            </div>
          ) : cases.length === 0 ? (
            <div className={styles.emptyState}>
              <p>No cases yet</p>
              <button className={styles.emptyNewButton} onClick={handleCreateCase}>
                Create your first case
              </button>
            </div>
          ) : (
            cases.map((caseData) => (
              <CaseListItem
                key={caseData.id}
                caseData={caseData}
                onDelete={handleDeleteCase}
              />
            ))
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={!!deleteTarget}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Case"
        message={`Are you sure you want to delete the case for ${deletingCase?.borrowerName || 'this borrower'}? This will permanently delete all associated data including documents, calculations, and progress. This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        isDangerous={true}
        isLoading={isDeleting}
      />
    </div>
  )
}

export default CaseListSidebar
